import type { IUserFilteredInfo } from '../../../../types/users'

/** Body text and header text, measured per character: 14px running text, 12px uppercase headers
 * with letter-spacing. An estimate rather than a DOM measurement - measuring would mean rendering
 * every value off-screen on every page of rows, to land within a few pixels of this. */
const BODY_CHAR_PX = 8
const HEADER_CHAR_PX = 9
/** The grid's body cell padding, both sides. */
const CELL_PADDING_PX = 32
/** Past this a single long email would push every other column off the screen. */
const MAX_WIDTH_PX = 320

/** Room a column's content needs beyond its text: the avatar beside a name, the editor's arrow on
 * Department and Location. */
const CHROME_PX: Record<string, number> = { name: 44, team: 24, location: 24 }

/** The pencil an empty editable cell shows beside its placeholder. */
const PLACEHOLDER_CHROME_PX = 24

/** A tag's own padding and border, plus the gap between it and the value it sits beside. */
const BADGE_CHROME_PX = 24
/** Tag text is a size down from body text. */
const BADGE_CHAR_PX = 7

/** The columns whose text is on the row. The rest keep their set width: role, labels, status and
 * dates render words the row does not carry, and the row-menu strip keeps its room on purpose. */
const TEXT_OF: Record<string, (user: IUserFilteredInfo) => string | undefined> = {
  name: (user) => user.name,
  team: (user) => user.team?.name,
  location: (user) => user.location?.name,
  email: (user) => user.email,
  employeeId: (user) => user.employeeId,
}

export interface IFitColumnWidthsParams {
  defaults: Record<string, number>
  users: IUserFilteredInfo[]
  /** Each column's header as the reader sees it, keyed by column id. */
  headers: Record<string, string>
  /** What an empty cell shows instead ("Add Employee ID"), keyed by column id. It needs room too,
   * or it wraps. */
  placeholders?: Record<string, string>
  /** Labels of the tags drawn beside a column's value, keyed by column id - pass the most heavily
   * decorated row's. They share the cell's flex row and are the one thing in it that cannot
   * shrink, so a column measured from its text alone leaves them to push the text out of it. */
  badges?: Record<string, string[]>
}

/**
 * Each text column as wide as its longest loaded value or its header, whichever is wider, and never
 * more than 320px. Until rows arrive there is nothing to measure, so the defaults stand.
 */
export function fitColumnWidths(params: IFitColumnWidthsParams): Record<string, number> {
  const { defaults, users, headers, placeholders = {}, badges = {} } = params
  if (users.length === 0) {
    return defaults
  }
  const widths = { ...defaults }
  for (const [columnId, textOf] of Object.entries(TEXT_OF)) {
    if (!(columnId in defaults)) {
      continue
    }
    const longest = users.reduce((max, user) => Math.max(max, (textOf(user) ?? '').length), 0)
    const anyEmpty = users.some((user) => !textOf(user))
    const placeholder = anyEmpty && placeholders[columnId] ? placeholders[columnId].length * BODY_CHAR_PX + CELL_PADDING_PX + PLACEHOLDER_CHROME_PX : 0
    const badgeRoom = (badges[columnId] ?? []).reduce((sum, label) => sum + label.length * BADGE_CHAR_PX + BADGE_CHROME_PX, 0)
    const body = Math.max(longest * BODY_CHAR_PX + CELL_PADDING_PX + (CHROME_PX[columnId] ?? 0) + badgeRoom, placeholder)
    const header = (headers[columnId] ?? '').length * HEADER_CHAR_PX + CELL_PADDING_PX
    widths[columnId] = Math.min(MAX_WIDTH_PX, Math.ceil(Math.max(body, header)))
  }
  return widths
}
